import { Injectable, inject } from '@angular/core';
import { AITool } from '../tool.decorator';
import { AccountOfflineService } from '@omni/services/account/account.offline.service';
import { ContactOfflineService } from '@omni/services/contact/contact.service';
import { CallPlanOfflineService } from '@omni/services/call-plan/call-plan.offline.service';
import { DiskService } from '@omni/services/disk/disk.service';
import { MarketScanService } from '@omni/services/market-scan/market-scan.service';
import { DB_KEY_PREFIXES } from '@omni/config/pouch-db.config';

/**
 * Call-plan readouts — list and per-plan deep-dive. Sources are
 * `callPlanService.presentCallPlans()` for the live "current" window and the
 * on-disk PouchDB doc (`MY_POSITON_CALL_PLANS`) re-aggregated via
 * `aggregateRepCallPlans` for past/future/all and detail lookups.
 */
@Injectable({ providedIn: 'root' })
export class CallPlanTools {
  private readonly accountService = inject(AccountOfflineService);
  private readonly contactService = inject(ContactOfflineService);
  private readonly callPlanService = inject(CallPlanOfflineService);
  private readonly diskService = inject(DiskService);
  private readonly marketScanService = inject(MarketScanService);

  /** The customer's segment (tier) for a call plan's contact, from the rep's position-scoped
   *  MarketScan segmentations — so call-plan progress can be grouped by segment. Null when the
   *  plan has no contact or the contact carries no segment. */
  private segmentForContact(contactId?: string): string | null {
    if (!contactId) return null;
    const match = (this.marketScanService.customerSegmentations || [])
      .find((s: any) => s.contactId === contactId && s.indskr_name);
    return (match as any)?.indskr_name ?? null;
  }

  @AITool(
    'get_my_call_plans',
    "Get the user's call plans (each with the customer's segment/tier), optionally filtered by time period (current, past, future). Group by segment to answer \"call-plan progress by segment\".",
    [
      {
        name: 'timeframe',
        type: 'string',
        description: 'Filter by time period: "current" (active now), "past" (ended), "future" (upcoming), or "all"',
        required: false,
        enum: ['current', 'past', 'future', 'all'],
        default: 'current',
      },
      { name: 'limit', type: 'number', description: 'Maximum number of results to return', required: false, default: 20 },
    ],
  )
  async getMyCallPlans(
    timeframe: 'current' | 'past' | 'future' | 'all' = 'current',
    limit = 20,
  ): Promise<any> {
    try {
      let filteredPlans: any[] = [];

      if (timeframe === 'current') {
        filteredPlans = await this.callPlanService.presentCallPlans('all');
      } else {
        const callPlansDoc = await this.diskService.retrieve(DB_KEY_PREFIXES.MY_POSITON_CALL_PLANS);

        if (!callPlansDoc || !callPlansDoc.raw || callPlansDoc.raw.length === 0) {
          return {
            success: true,
            message: 'No call plans found',
            callPlans: [],
          };
        }

        const aggregatedPlans = this.callPlanService.aggregateRepCallPlans(callPlansDoc.raw);
        const currentTime = new Date().getTime();

        aggregatedPlans.forEach(plan => {
          const startTime = new Date(plan.indskr_startdate).getTime();
          const endTime = new Date(plan.indskr_enddate).getTime();

          if (timeframe === 'past' && endTime < currentTime) {
            filteredPlans.push(plan);
          } else if (timeframe === 'future' && startTime > currentTime) {
            filteredPlans.push(plan);
          } else if (timeframe === 'all') {
            filteredPlans.push(plan);
          }
        });
      }

      if (filteredPlans.length === 0) {
        // Current window empty ≠ no data. Models routinely call this tool with no args
        // (default "current"), so a bare "none found" dead-ends questions like
        // "what is my progress %". Surface what DOES exist so the reply stays grounded.
        if (timeframe === 'current') {
          const doc = await this.diskService.retrieve(DB_KEY_PREFIXES.MY_POSITON_CALL_PLANS);
          const aggregated = doc?.raw?.length ? this.callPlanService.aggregateRepCallPlans(doc.raw) : [];
          const now = Date.now();
          const past = aggregated
            .filter(p => new Date(p.indskr_enddate).getTime() < now)
            .sort((a, b) => new Date(b.indskr_enddate).getTime() - new Date(a.indskr_enddate).getTime());
          const future = aggregated.filter(p => new Date(p.indskr_startdate).getTime() > now);
          return {
            success: true,
            message: `No call plans are active right now. ${past.length} past and ${future.length} future plans exist — the most recent past plans (with completion) are included; pass timeframe "past", "future" or "all" for the full list.`,
            callPlans: [],
            pastPlanCount: past.length,
            futurePlanCount: future.length,
            mostRecentPastPlans: past.slice(0, 3).map(p => ({
              customerName: p.customerName,
              cyclePlanName: p.cycleplanname,
              startDate: p.indskr_startdate,
              endDate: p.indskr_enddate,
              completionPercentage: p.percentage || 0,
              completedGoals: p.completedGoals || 0,
              definedGoals: p.definedGoals || 0,
            })),
          };
        }
        return {
          success: true,
          message: `No ${timeframe === 'all' ? '' : timeframe + ' '}call plans found`,
          callPlans: [],
        };
      }

      let totalCompletedGoals = 0;
      let totalDefinedGoals = 0;
      let customersWithReach = 0;
      const totalCustomers = filteredPlans.length;
      let totalInteractions = 0;
      let interactedCustomers = 0;

      // Days-left countdown only when every plan shares one end date.
      let daysLeft = -1;
      const uniqueEndDates = new Set<string>();
      for (const plan of filteredPlans) {
        if (plan.indskr_enddate) uniqueEndDates.add(plan.indskr_enddate as string);
        for (const repPlan of plan.repPlans || []) {
          if (repPlan.indskr_enddate) uniqueEndDates.add(repPlan.indskr_enddate as string);
        }
      }

      if (uniqueEndDates.size === 1) {
        const endDate = new Date([...uniqueEndDates][0]!);
        const today = new Date();
        const timeDiff = endDate.getTime() - today.getTime();
        const daysDiff = Math.ceil(timeDiff / (1000 * 3600 * 24));

        if (daysDiff <= 3 && daysDiff >= 0) {
          daysLeft = daysDiff;
        }
      }

      for (const plan of filteredPlans) {
        totalCompletedGoals += plan.completedGoals || 0;
        totalDefinedGoals += plan.definedGoals || 0;

        if (plan.percentage && plan.percentage > 0) {
          customersWithReach++;
        }

        const goalF2F = plan.totalGoalF2FMeetings || 0;
        const goalRemote = plan.totalGoalRemoteMeetings || 0;
        const goalEvent = plan.totalGoalEvents || 0;

        const actualF2F = plan.actualF2FMeetings || 0;
        const actualRemote = plan.actualRemoteMeetings || 0;
        const actualEvent = plan.actualEvents || 0;

        // Cap actuals at their respective goals so a wildly overshot
        // category doesn't inflate the average-frequency metric.
        const customerInteractions =
          Math.min(actualF2F, goalF2F) +
          Math.min(actualRemote, goalRemote) +
          Math.min(actualEvent, goalEvent);

        if (customerInteractions > 0) {
          totalInteractions += customerInteractions;
        }
        if (goalF2F + goalRemote + goalEvent > 0) {
          interactedCustomers++;
        }
      }

      const avgFrequency =
        interactedCustomers > 0 ? Math.round((totalInteractions / interactedCustomers) * 10) / 10 : 0;
      const progressPercentage =
        totalDefinedGoals > 0 ? Math.round((totalCompletedGoals / totalDefinedGoals) * 100) : 0;

      const limitedPlans = filteredPlans.slice(0, limit);

      const formattedResult = limitedPlans.map(plan => ({
        id: plan.customerId || plan.contactId || plan.accountId,
        customerName: plan.customerName,
        customerType: plan.customerType,
        segment: this.segmentForContact(plan.contactId ?? plan.customerId),
        cyclePlanName: plan.cycleplanname,
        startDate: plan.indskr_startdate,
        endDate: plan.indskr_enddate,
        productName: plan.productName,
        completionPercentage: plan.percentage || 0,
        goals: {
          totalCalls: plan.totalGoalCalls || 0,
          totalEmails: plan.totalGoalEmails || 0,
          f2fMeetings: plan.totalGoalF2FMeetings || 0,
          remoteMeetings: plan.totalGoalRemoteMeetings || 0,
          phoneCalls: plan.totalGoalPhoneCalls || 0,
          events: plan.totalGoalEvents || 0,
          totalGoals: plan.definedGoals || 0,
        },
        actuals: {
          calls: plan.actualCalls || 0,
          emails: plan.actualEmails || 0,
          f2fMeetings: plan.actualF2FMeetings || 0,
          remoteMeetings: plan.actualRemoteMeetings || 0,
          phoneCalls: plan.actualPhoneCalls || 0,
          events: plan.actualEvents || 0,
          totalCompleted: plan.completedGoals || 0,
        },
      }));

      return {
        success: true,
        count: formattedResult.length,
        timeframe,
        totalAvailable: filteredPlans.length,
        metrics: {
          daysLeft,
          goalCompletion: totalCompletedGoals,
          totalGoal: totalDefinedGoals,
          reach: customersWithReach,
          totalReach: totalCustomers,
          avgFrequency,
          progressPercentage,
        },
        callPlans: formattedResult,
      };
    } catch (error) {
      console.error('[CallPlanTools] Error getting call plans:', error);
      return { success: false, error: error instanceof Error ? error.message : 'Failed to get call plans' };
    }
  }

  @AITool(
    'get_call_plan_details',
    'Get comprehensive detailed information about a specific call plan including all goals, actuals, completion percentages, products, and customer information',
    [
      {
        name: 'call_plan_id',
        type: 'string',
        description: 'The customer ID, contact ID, account ID, or customer name for the call plan',
        required: true,
      },
    ],
  )
  async getCallPlanDetails(call_plan_id: string): Promise<any> {
    try {
      const callPlansDoc = await this.diskService.retrieve(DB_KEY_PREFIXES.MY_POSITON_CALL_PLANS);

      if (!callPlansDoc || !callPlansDoc.raw || callPlansDoc.raw.length === 0) {
        return { success: false, message: 'No call plans data available' };
      }

      const allPlans = this.callPlanService.aggregateRepCallPlans(callPlansDoc.raw);

      const callPlan = allPlans.find(plan =>
        plan.customerId === call_plan_id ||
        plan.contactId === call_plan_id ||
        plan.accountId === call_plan_id ||
        plan.cycleplanid === call_plan_id ||
        plan.customerName?.toLowerCase().includes(call_plan_id.toLowerCase()),
      );

      if (!callPlan) {
        return { success: false, message: `Call plan not found for: ${call_plan_id}` };
      }

      let customerDetails: any = null;
      if (callPlan.contactId) {
        customerDetails = this.contactService.getContactByID(callPlan.contactId);
      } else if (callPlan.accountId) {
        const accounts = this.accountService.accounts || [];
        customerDetails = accounts.find(a => a.id === callPlan.accountId);
      }

      const detailedInfo = {
        customerName: callPlan.customerName,
        customerType: callPlan.customerType,
        customerId: callPlan.customerId,
        contactId: callPlan.contactId,
        accountId: callPlan.accountId,
        cyclePlanName: callPlan.cycleplanname,
        cyclePlanId: callPlan.cycleplanid,
        startDate: callPlan.indskr_startdate,
        endDate: callPlan.indskr_enddate,
        completionPercentage: callPlan.percentage || 0,
        allPlansApproved: callPlan.allRepCallPlansApproved || false,
        customerInfo: customerDetails
          ? {
              fullName: customerDetails.fullName || customerDetails.name,
              specialty: customerDetails.specialty || customerDetails.accountType,
              address: customerDetails.primaryAddress,
            }
          : null,
        summary: {
          totalDefinedGoals: callPlan.definedGoals || 0,
          totalCompletedGoals: callPlan.completedGoals || 0,
          completionPercentage: callPlan.percentage || 0,
        },
        goals: {
          calls: { goal: callPlan.totalGoalCalls || 0, actual: callPlan.actualCalls || 0 },
          emails: { goal: callPlan.totalGoalEmails || 0, actual: callPlan.actualEmails || 0 },
          f2fMeetings: { goal: callPlan.totalGoalF2FMeetings || 0, actual: callPlan.actualF2FMeetings || 0 },
          remoteMeetings: {
            goal: callPlan.totalGoalRemoteMeetings || 0,
            actual: callPlan.actualRemoteMeetings || 0,
          },
          phoneCalls: { goal: callPlan.totalGoalPhoneCalls || 0, actual: callPlan.actualPhoneCalls || 0 },
          events: { goal: callPlan.totalGoalEvents || 0, actual: callPlan.actualEvents || 0 },
        },
        repPlans:
          callPlan.repPlans?.map((rp: any) => ({
            callPlanId: rp.indskr_customercallplanid,
            product: rp.productid_Formatted || rp.productName,
            segment: rp.segmentid_Formatted || rp.segmentName,
            specialty: rp.specialtyid_Formatted,
            position: rp.positionName,
            status: rp.statuscode,
            goals: {
              calls: rp.indskr_hocalls || 0,
              emails: rp.indskr_hoemails || 0,
              f2fMeetings: rp.indskr_f2fgoals || 0,
              remoteMeetings: rp.indskr_remotemeetinggoals || 0,
              phoneCalls: rp.indskr_phonecallgoals || 0,
              events: rp.indskr_eventgoals || 0,
            },
            actuals: {
              calls: rp.indskr_actualcalls || 0,
              emails: rp.indskr_actualemails || 0,
              f2fMeetings: rp.totalf2fmeetingscompleted || 0,
              remoteMeetings: rp.totalremotemeetingscompleted || 0,
              phoneCalls: rp.totalphonecallscompleted || 0,
              events: rp.indskr_completedevents || 0,
            },
            timeRemaining: rp.indskr_timeremaining,
          })) || [],
      };

      return { success: true, callPlan: detailedInfo };
    } catch (error) {
      console.error('[CallPlanTools] Error getting call plan details:', error);
      return { success: false, error: error instanceof Error ? error.message : 'Failed to get call plan details' };
    }
  }
}
